import { useState, useEffect, useRef, useCallback } from 'react';
import { MapPin, Loader2, X } from 'lucide-react';
import { Input } from './ui/input';

// This component provides address autocomplete functionality
// Uses the NEW Google Places API (Place class) to avoid deprecation warnings
// Falls back to manual input if API key not configured

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Supported European countries (ISO 3166-1 alpha-2 codes)
const SUPPORTED_COUNTRIES = [
  'gb', 'ie', 'fr', 'de', 'es', 'it', 'pt', 'nl', 'be', 'at', 'ch', 'pl',
  'cz', 'dk', 'se', 'no', 'fi', 'gr', 'hu', 'ro', 'bg', 'hr', 'sk', 'si',
  'ee', 'lv', 'lt', 'lu', 'mt', 'cy',
];

// Global flag to prevent multiple script loads
let googleMapsScriptLoaded = false;
let googleMapsScriptLoading = false;
const loadCallbacks = [];

// Check if Google Maps is ready to use
const isGoogleMapsReady = () => {
  return window.google?.maps?.places?.AutocompleteService || 
         window.google?.maps?.places?.AutocompleteSuggestion;
};

export function AddressAutocomplete({ 
  value = '', 
  onChange, 
  onSelect, 
  placeholder = 'Enter address...',
  className = '',
  restrictToCountries = SUPPORTED_COUNTRIES,
  types = ['geocode'],
}) {
  const [inputValue, setInputValue] = useState(value);
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isGoogleLoaded, setIsGoogleLoaded] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [useLegacyAPI, setUseLegacyAPI] = useState(false);
  const autocompleteService = useRef(null);
  const sessionToken = useRef(null);
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);
  const debounceTimer = useRef(null);

  // Load Google Maps script once globally
  const loadGoogleMapsScript = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (googleMapsScriptLoaded && window.google?.maps?.places) {
        resolve();
        return;
      }

      if (googleMapsScriptLoading) {
        loadCallbacks.push({ resolve, reject });
        return;
      }

      if (!GOOGLE_MAPS_API_KEY || GOOGLE_MAPS_API_KEY === 'your_google_maps_api_key_here') {
        reject(new Error('No API key'));
        return;
      }

      // Check if already loaded by another component
      if (window.google?.maps?.places) {
        googleMapsScriptLoaded = true;
        resolve();
        return;
      }

      googleMapsScriptLoading = true;

      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places&loading=async`;
      script.async = true;
      script.defer = true;
      
      script.onload = () => {
        googleMapsScriptLoaded = true;
        googleMapsScriptLoading = false;
        resolve();
        loadCallbacks.forEach(cb => cb.resolve());
        loadCallbacks.length = 0;
      };
      
      script.onerror = (err) => {
        googleMapsScriptLoading = false;
        reject(err);
        loadCallbacks.forEach(cb => cb.reject(err));
        loadCallbacks.length = 0;
      };

      document.head.appendChild(script);
    });
  }, []);

  // Initialize services
  useEffect(() => {
    const init = async () => {
      setIsInitializing(true);
      try {
        // Check if Google Maps is already loaded by another component
        if (isGoogleMapsReady()) {
          if (window.google.maps.places.AutocompleteSuggestion) {
            setUseLegacyAPI(false);
            setIsGoogleLoaded(true);
            console.log('AddressAutocomplete: Using NEW Places API');
          } else {
            setUseLegacyAPI(true);
            autocompleteService.current = new window.google.maps.places.AutocompleteService();
            sessionToken.current = new window.google.maps.places.AutocompleteSessionToken();
            setIsGoogleLoaded(true);
            console.log('AddressAutocomplete: Using LEGACY Places API');
          }
          setIsInitializing(false);
          return;
        }

        // Load script if not loaded
        await loadGoogleMapsScript();
        
        // Wait a bit for the API to initialize
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Check which API is available after loading
        if (window.google?.maps?.places?.AutocompleteSuggestion) {
          setUseLegacyAPI(false);
          setIsGoogleLoaded(true);
          console.log('AddressAutocomplete: Using NEW Places API (after load)');
        } else if (window.google?.maps?.places?.AutocompleteService) {
          setUseLegacyAPI(true);
          autocompleteService.current = new window.google.maps.places.AutocompleteService();
          sessionToken.current = new window.google.maps.places.AutocompleteSessionToken();
          setIsGoogleLoaded(true);
          console.log('AddressAutocomplete: Using LEGACY Places API (after load)');
        } else {
          console.log('AddressAutocomplete: No Places API available');
        }
      } catch (err) {
        console.log('AddressAutocomplete: Google Maps not available, using manual input', err);
      } finally {
        setIsInitializing(false);
      }
    };

    init();
  }, [loadGoogleMapsScript]);

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Sync external value changes
  useEffect(() => {
    if (value !== inputValue) {
      setInputValue(value);
    }
  }, [value]);

  // Fetch suggestions using NEW Place API (Autocomplete suggestions)
  const fetchSuggestionsNew = async (query) => {
    if (!query || query.length < 3) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);

    try {
      const { Place, AutocompleteSuggestion } = window.google.maps.places;
      
      // Use the new AutocompleteSuggestion API
      const request = {
        input: query,
        includedPrimaryTypes: types.includes('geocode') ? ['geocode'] : types,
        includedRegionCodes: restrictToCountries.length > 0 ? restrictToCountries.map(c => c.toUpperCase()) : undefined,
      };

      const { suggestions: autocompleteSuggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

      if (autocompleteSuggestions && autocompleteSuggestions.length > 0) {
        const mappedSuggestions = autocompleteSuggestions.map((suggestion, index) => ({
          id: index,
          placeId: suggestion.placePrediction?.placeId || null,
          description: suggestion.placePrediction?.text?.text || '',
          mainText: suggestion.placePrediction?.mainText?.text || suggestion.placePrediction?.text?.text || '',
          secondaryText: suggestion.placePrediction?.secondaryText?.text || '',
          placePrediction: suggestion.placePrediction,
        }));
        
        setSuggestions(mappedSuggestions);
        setShowSuggestions(true);
      } else {
        setSuggestions([]);
      }
    } catch (error) {
      console.error('Error fetching suggestions with new API:', error);
      // Fall back to legacy API on error
      if (window.google?.maps?.places?.AutocompleteService) {
        setUseLegacyAPI(true);
        autocompleteService.current = new window.google.maps.places.AutocompleteService();
        sessionToken.current = new window.google.maps.places.AutocompleteSessionToken();
        fetchSuggestionsLegacy(query);
      } else {
        setSuggestions([]);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch suggestions using LEGACY API (fallback)
  const fetchSuggestionsLegacy = async (query) => {
    if (!query || query.length < 3 || !autocompleteService.current) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);

    try {
      autocompleteService.current.getPlacePredictions(
        {
          input: query,
          sessionToken: sessionToken.current,
          types: types,
          componentRestrictions: restrictToCountries.length > 0 
            ? { country: restrictToCountries } 
            : undefined,
        },
        (predictions, status) => {
          setIsLoading(false);
          if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
            setSuggestions(predictions.map((p, index) => ({
              id: index,
              placeId: p.place_id,
              description: p.description,
              mainText: p.structured_formatting?.main_text || p.description,
              secondaryText: p.structured_formatting?.secondary_text || '',
            })));
            setShowSuggestions(true);
          } else {
            setSuggestions([]);
          }
        }
      );
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      setIsLoading(false);
      setSuggestions([]);
    }
  };

  // Get place details using NEW Place API
  const getPlaceDetailsNew = async (suggestion) => {
    const { Place } = window.google.maps.places;
    
    try {
      // Create Place object from prediction
      const place = suggestion.placePrediction?.toPlace?.() || new Place({ id: suggestion.placeId });
      
      // Fetch required fields
      await place.fetchFields({
        fields: ['displayName', 'formattedAddress', 'location', 'addressComponents']
      });

      const addressData = {
        address: place.formattedAddress || suggestion.description,
        latitude: place.location?.lat() || null,
        longitude: place.location?.lng() || null,
        placeId: suggestion.placeId,
        components: parseAddressComponentsNew(place.addressComponents),
      };
      
      handleSelect(addressData);
    } catch (error) {
      console.error('Error getting place details with new API:', error);
      // Fallback to just using the description
      handleSelect({ address: suggestion.description });
    }
  };

  // Get place details using LEGACY API
  const getPlaceDetailsLegacy = (placeId, description) => {
    const placesService = new window.google.maps.places.PlacesService(document.createElement('div'));
    
    placesService.getDetails(
      {
        placeId: placeId,
        fields: ['formatted_address', 'geometry', 'address_components'],
        sessionToken: sessionToken.current,
      },
      (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
          const addressData = {
            address: place.formatted_address || description,
            latitude: place.geometry?.location?.lat() || null,
            longitude: place.geometry?.location?.lng() || null,
            placeId: placeId,
            components: parseAddressComponentsLegacy(place.address_components),
          };
          handleSelect(addressData);
          sessionToken.current = new window.google.maps.places.AutocompleteSessionToken();
        } else {
          handleSelect({ address: description });
        }
      }
    );
  };

  // Parse address components from NEW API (camelCase fields)
  const parseAddressComponentsNew = (components) => {
    if (!components) return {};
    
    const result = {};
    components.forEach(comp => {
      const types = comp.types || [];
      if (types.includes('street_number')) {
        result.streetNumber = comp.longText || comp.shortText;
      }
      if (types.includes('route')) {
        result.street = comp.longText || comp.shortText;
      }
      if (types.includes('locality') || types.includes('postal_town')) {
        result.city = comp.longText || comp.shortText;
      }
      if (types.includes('postal_code')) {
        result.postalCode = comp.longText || comp.shortText;
      }
      if (types.includes('country')) {
        result.country = comp.longText;
        result.countryCode = comp.shortText;
      }
    });
    return result;
  };

  // Parse address components from LEGACY API
  const parseAddressComponentsLegacy = (components) => {
    if (!components) return {};
    
    const result = {};
    components.forEach(comp => {
      if (comp.types.includes('street_number')) {
        result.streetNumber = comp.long_name;
      }
      if (comp.types.includes('route')) {
        result.street = comp.long_name;
      }
      if (comp.types.includes('locality') || comp.types.includes('postal_town')) {
        result.city = comp.long_name;
      }
      if (comp.types.includes('postal_code')) {
        result.postalCode = comp.long_name;
      }
      if (comp.types.includes('country')) {
        result.country = comp.long_name;
        result.countryCode = comp.short_name;
      }
    });
    return result;
  };

  const handleSelect = (addressData) => {
    setInputValue(addressData.address || '');
    setShowSuggestions(false);
    setSuggestions([]);
    
    if (onChange) {
      onChange(addressData.address);
    }
    if (onSelect) {
      onSelect(addressData);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    if (useLegacyAPI) {
      getPlaceDetailsLegacy(suggestion.placeId, suggestion.description);
    } else {
      getPlaceDetailsNew(suggestion);
    }
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    
    if (onChange) {
      onChange(newValue);
    }

    // Debounce the API call
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    
    debounceTimer.current = setTimeout(() => {
      if (isGoogleLoaded) {
        if (useLegacyAPI) {
          fetchSuggestionsLegacy(newValue);
        } else {
          fetchSuggestionsNew(newValue);
        }
      }
    }, 300);
  };

  const clearInput = () => {
    setInputValue('');
    setSuggestions([]);
    setShowSuggestions(false);
    if (onChange) onChange('');
    if (onSelect) onSelect(null);
    inputRef.current?.focus();
  };

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <Input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
          placeholder={placeholder}
          className="pl-10 pr-10"
          autoComplete="off"
          data-testid="address-autocomplete-input"
        />
        {inputValue && (
          <button
            type="button"
            onClick={clearInput}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
          >
            <X className="w-4 h-4" />
          </button>
        )}
        {isLoading && (
          <Loader2 className="absolute right-10 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 animate-spin" />
        )}
      </div>

      {/* Suggestions dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-auto">
          {suggestions.map((suggestion) => (
            <button
              key={suggestion.id}
              type="button"
              className="w-full px-4 py-3 text-left hover:bg-slate-50 focus:bg-slate-50 focus:outline-none border-b border-slate-100 last:border-b-0"
              onClick={() => handleSuggestionClick(suggestion)}
              data-testid={`address-suggestion-${suggestion.id}`}
            >
              <div className="font-medium text-slate-900">{suggestion.mainText}</div>
              {suggestion.secondaryText && (
                <div className="text-sm text-slate-500">{suggestion.secondaryText}</div>
              )}
            </button>
          ))}
          {isGoogleLoaded && (
            <div className="px-4 py-2 text-xs text-slate-400 bg-slate-50 flex items-center justify-end gap-1">
              <img 
                src="https://developers.google.com/static/maps/documentation/images/powered_by_google_on_white.png" 
                alt="Powered by Google" 
                className="h-3"
              />
            </div>
          )}
        </div>
      )}

      {/* No Google Maps notice - only show after initialization is complete */}
      {!isInitializing && !isGoogleLoaded && inputValue.length >= 3 && (
        <p className="mt-1 text-xs text-slate-500">
          <MapPin className="w-3 h-3 inline mr-1" />
          Enter your full delivery address manually
        </p>
      )}
    </div>
  );
}

export default AddressAutocomplete;
